import { useState, type CSSProperties } from 'react';

export interface AvatarProps {
  src?: string;
  alt?: string;
  name?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  online?: boolean;
  className?: string;
}

const sizeMap = { sm: 24, md: 32, lg: 48, xl: 64 } as const;

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function Avatar({ src, alt, name, size = 'md', online, className }: AvatarProps) {
  const [imgError, setImgError] = useState(false);
  const px = sizeMap[size];
  const showImage = src && !imgError;

  const containerStyle: CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: px,
    height: px,
    borderRadius: '50%',
    backgroundColor: showImage ? 'transparent' : '#6366f1',
    color: '#ffffff',
    fontSize: px * 0.4,
    fontWeight: 600,
    overflow: 'hidden',
    flexShrink: 0,
  };

  const dotStyle: CSSProperties = {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: Math.max(8, px * 0.25),
    height: Math.max(8, px * 0.25),
    borderRadius: '50%',
    backgroundColor: '#22c55e',
    border: '2px solid #ffffff',
  };

  return (
    <div role="img" aria-label={alt ?? name ?? 'Avatar'} data-size={size} className={className} style={containerStyle}>
      {showImage ? (
        <img
          src={src}
          alt={alt ?? name ?? 'Avatar'}
          onError={() => setImgError(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <span aria-hidden="true">{name ? getInitials(name) : '?'}</span>
      )}
      {online !== undefined && <span data-testid="online-dot" style={dotStyle} />}
    </div>
  );
}
